/* eslint-disable no-magic-numbers, class-methods-use-this */
import Phase from '../phase-core/Phase';

const { pow } = Math;

class AudioFactory {

	sources = new Set();

	constructor(audio) {
		this.audio = audio;
	}

	assign(obj, prop, value) {
		if (!(prop in obj)) {
			throw new Error(`Invalid property: ${prop}`);
		}
		if (typeof value === 'number') {
			obj[prop].value = value;
		} else if (value instanceof AudioNode) {
			value.connect(obj[prop]);
		} else {
			throw new Error('Invalid value');
		}
	}

	makeConst(level) {
		const el = this.audio.createConstantSource();
		this.assign(el, 'offset', level);
		this.sources.add(el);
		return el;
	}

	makeSine(frequency) {
		const el = this.audio.createOscillator();
		el.type = 'sine';
		this.assign(el, 'frequency', frequency);
		this.sources.add(el);
		return el;
	}

	makeGain(level) {
		const el = this.audio.createGain();
		this.assign(el, 'gain', level);
		return el;
	}

	makeDelay(time) {
		const el = this.audio.createDelay();
		this.assign(el, 'delayTime', time);
		return el;
	}

	mix(...sources) {
		const gain = this.makeGain(1);
		for (const source of sources) {
			if (source) {
				source.connect(gain);
			}
		}
		return gain;
	}

	chain(...els) {
		return els.reduce((prev, next) => {
			if (prev && next) {
				prev.connect(next);
			}
			return next !== null ? next : prev;
		}, null);
	}

	makeSineOff(freq, mag = 1, off = 0) {
		return this.mix(
			this.chain(
				this.makeSine(freq),
				mag !== 1 ? this.makeGain(mag) : null),
			off !== 0 ? this.makeConst(off) : null);
	}

	makeFeedback(source, sink, ...delays) {
		for (const [delay, atten] of delays) {
			this.chain(source, this.makeDelay(delay), this.makeGain(atten), sink);
		}
	}

	takeSources() {
		const src = [...this.sources];
		this.sources.clear();
		return src;
	}

	makeConfigurableSine(f, level) {
		const osc = this.makeSine(f);
		const gain = this.makeGain(level);
		osc.connect(gain);
		this.sources.delete(osc);
		return new ConfigurableSine(osc, gain);
	}

}

class ConfigurableSine {

	constructor(osc, gain) {
		this.osc = osc;
		this.gain = gain;
	}

	connect(...args) {
		this.gain.connect(...args);
	}

	start() {
		this.osc.start();
	}

	stop() {
		this.osc.stop();
	}

	getGainParam() {
		return this.gain.gain;
	}

}

export default class Feedback extends Phase {

	audio = null;
	mixer = null;
	master = null;
	feedback = null;
	factory = null;

	sines = null;

	constructor(audio) {
		super(length);
		const factory = new AudioFactory(audio);

		const master = factory.makeGain(0);
		const mixer = factory.makeGain(1);
		const tremolo = factory.makeGain(factory.makeSineOff(2, 0.1, 0.9));
		factory.chain(
			mixer,
			tremolo,
			master,
			audio.destination);

		const feedback = factory.makeGain(0);
		factory.makeFeedback(mixer, feedback, [0.02, 0.4], [0.05, 0.4], [0.18, 0.7], [0.24, 0.5]);
		factory.chain(feedback, mixer);

		this.audio = audio;
		this.mixer = mixer;
		this.master = master;
		this.feedback = feedback;
		this.factory = factory;
		this.sines = new Map();
	}

	defaultOpts() {
		return {
			master_level: 0,
			feedback_level: 0,
			speed: 0,
			spectrum: []
		};
	}

	setSines(map, time) {
		const prev = new Set(this.sines.keys());
		const next = new Set(map.keys());
		const add = [...next].filter(f => !prev.has(f));
		const del = [...prev].filter(f => !next.has(f));
		const mod = [...next].filter(f => prev.has(f));
		const level = db => pow(10, db / 20);
		add.forEach(f => {
			const db = map.get(f);
			const sine = this.factory.makeConfigurableSine(f, level(db));
			sine.connect(this.mixer);
			this.sines.set(f, sine);
			sine.start();
		});
		del.forEach(f => {
			const sine = this.sines.get(f);
			this.sines.delete(f);
			sine.stop();
		});
		mod.forEach(f => {
			const db = map.get(f);
			const sine = this.sines.get(f);
			sine.getGainParam().linearRampToValueAtTime(level(db), time);
		});
	}

	play({ master_level, feedback_level, spectrum, speed }) {
		const { audio, master, feedback } = this;
		this.setSines(spectrum, audio.currentTime + speed);
		const set = (prop, value) => {
			prop.value = value;
			prop.setValueAtTime(value, audio.currentTime);
		};
		const fade = (prop, value) => {
			if (speed <= 0) {
				set(prop, value);
			} else {
				prop.linearRampToValueAtTime(value, audio.currentTime + speed);
			}
		};
		fade(master.gain, master_level);
		fade(feedback.gain, feedback_level);
	}

	stop() {
		this.play(this.defaultOpts());
	}

}
